import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  CircularProgress,
  Tooltip,
  Grid,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { SvgIconComponent } from '@mui/icons-material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import PetsIcon from '@mui/icons-material/Pets';
import PestControlIcon from '@mui/icons-material/PestControl';
import ApiIcon from '@mui/icons-material/Api';
import DashboardIcon from '@mui/icons-material/Dashboard';
import { useAuth } from '../../contexts/AuthContext';
import { useBusiness } from '../../contexts/BusinessContext';
import { useSettingsDialog } from '../../contexts/SettingsDialogContext';
import { UserInfo } from '../UserSettingsDialog';
import OptionCard from '../onboarding/OptionCard';

interface SubscriptionType {
  id: string;
  code: string;
  name: string;
  description: string | null;
  price: number;
  durationInDays: number | null;
  isTrial: boolean;
  planFamily: string;
  includesHardwareRental: boolean;
  includesApiAccess: boolean;
  stripePriceId: string | null;
  archivedDate: string | null;
}

interface BusinessSubscription {
  id: string;
  businessId: string;
  subscriptionTypeId: string;
  subscriptionTypeCode: string;
  subscriptionTypeName: string;
  planFamily: string;
  isTrial: boolean;
  dateStart: string;
  dateEnd: string | null;
  isActive: boolean;
  status: string;
  cancelledDate: string | null;
  paymentProvider: string | null;
  autoRenew: boolean;
  nextBillingDate: string | null;
}

interface TrapRentalRequestSummary {
  id: string;
  planFamily: string;
  status: string;
  startDate: string;
  endDate: string;
  estimatedCost: number;
}

interface Props {
  userInfo: UserInfo;
}

// A business can hold one active (and one ever-trialed) subscription per
// product family concurrently — see api's SubscriptionType.PlanFamily.
const FAMILY_LABELS: Record<string, string> = {
  INFERENCE_NOTIFY: 'Inference + Notification',
  PIG_TRAP_RENTAL: 'Pig Trap Rental',
  FOXCAT_TRAP_RENTAL: 'Fox/Cat Trap Rental',
  API_ACCESS: 'API Access',
};

const familyLabel = (family: string) => FAMILY_LABELS[family] ?? family;

const isCurrentlyActive = (sub: BusinessSubscription) =>
  sub.status === 'Active' && (sub.dateEnd === null || new Date(sub.dateEnd) > new Date());

const TRAP_FAMILIES = new Set(['PIG_TRAP_RENTAL', 'FOXCAT_TRAP_RENTAL']);

type CategoryKey = 'TRAP_RENTAL' | 'CAMERA_DETECTION' | 'API_ACCESS';

// The readme's sc006 ask — three top-level product categories, each a big
// icon card, rather than a flat grid of every plan tier at once. Purely a
// browsing layer: doesn't change what the API returns or how a plan starts.
const CATEGORY_META: Record<CategoryKey, { label: string; caption: string; icon: SvgIconComponent; families: string[] }> = {
  CAMERA_DETECTION: {
    label: 'Camera Object Detection & Notification',
    caption: 'Use your own camera — we detect and text you.',
    icon: CameraAltIcon,
    families: ['INFERENCE_NOTIFY'],
  },
  TRAP_RENTAL: {
    label: 'Trap Rental',
    caption: 'Rent a pig or fox/cat trap with detection + SMS included.',
    icon: PestControlIcon,
    families: ['PIG_TRAP_RENTAL', 'FOXCAT_TRAP_RENTAL'],
  },
  API_ACCESS: {
    label: 'API Access',
    caption: 'Submit images to our classification API yourself.',
    icon: ApiIcon,
    families: ['API_ACCESS'],
  },
};

const TRAP_SUB_META: Record<string, { label: string; icon: SvgIconComponent }> = {
  PIG_TRAP_RENTAL: { label: 'Pig Trap', icon: PetsIcon },
  FOXCAT_TRAP_RENTAL: { label: 'Fox/Cat Trap', icon: PestControlIcon },
};

// Client-side estimate only, purely for display before submitting — the API
// recomputes this itself and never trusts a client-supplied cost.
const estimateRentalCost = (monthlyPrice: number, startDate: string, endDate: string) => {
  const days = (new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24);
  const months = Math.max(1, Math.ceil(days / 30));
  return monthlyPrice * months;
};

const BillingSubscriptionTab: React.FC<Props> = ({ userInfo }) => {
  const auth = useAuth();
  const { selectedBusinessId, selectedBusiness } = useBusiness();
  const { closeSettings } = useSettingsDialog();
  const authHeader = { Authorization: `Bearer ${auth.tokens?.accessToken}` };

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [planTypes, setPlanTypes] = useState<SubscriptionType[]>([]);
  const [subscriptions, setSubscriptions] = useState<BusinessSubscription[]>([]);
  const [startingPlanId, setStartingPlanId] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [checkingOutPlanId, setCheckingOutPlanId] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);

  const [category, setCategory] = useState<CategoryKey | null>(null);
  const [trapFamily, setTrapFamily] = useState<string | null>(null);

  const [myTrapRequests, setMyTrapRequests] = useState<TrapRentalRequestSummary[]>([]);
  const [requestFormPlanId, setRequestFormPlanId] = useState<string | null>(null);
  const [requestStart, setRequestStart] = useState('');
  const [requestEnd, setRequestEnd] = useState('');
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);

  const isVerified = userInfo.emailConfirmed && userInfo.phoneNumberConfirmed;

  const fetchAll = async () => {
    const [types, subs, trapRequests]: [SubscriptionType[], BusinessSubscription[], TrapRentalRequestSummary[]] = await Promise.all([
      fetch('/api/subscription-types', { headers: authHeader }).then((res) => (res.ok ? res.json() : [])),
      fetch(`/api/businesses/${selectedBusinessId}/subscriptions`, { headers: authHeader }).then((res) => (res.ok ? res.json() : [])),
      fetch('/api/trap-rental-requests/mine', { headers: authHeader }).then((res) => (res.ok ? res.json() : [])),
    ]);
    setPlanTypes(types.filter((t) => !t.archivedDate));
    setSubscriptions(subs);
    setMyTrapRequests(trapRequests);
    return subs;
  };

  const loadData = () => {
    if (!selectedBusinessId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    fetchAll()
      .catch(() => setError('Failed to load subscription information'))
      .finally(() => setLoading(false));
  };

  const reloadTrapRequests = async () => {
    try {
      const res = await fetch('/api/trap-rental-requests/mine', { headers: authHeader });
      if (res.ok) {
        setMyTrapRequests(await res.json());
      }
    } catch {
      // best-effort — the "already requested" banner just won't refresh if this fails
    }
  };

  useEffect(() => {
    loadData();
    setCategory(null);
    setTrapFamily(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBusinessId]);

  // Just back from Stripe Checkout — the activating subscription reaches us
  // via webhook, which can land a second or two after the redirect, so the
  // very first loadData() above may not see it yet. A few silent (no
  // spinner) background refetches smooth that over without a manual reload.
  useEffect(() => {
    if (!selectedBusinessId || sessionStorage.getItem('stripeCheckoutPending') !== '1') {
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const interval = setInterval(() => {
      attempts += 1;
      fetchAll().catch(() => {});
      if (cancelled || attempts >= 5) {
        clearInterval(interval);
        sessionStorage.removeItem('stripeCheckoutPending');
      }
    }, 2000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBusinessId]);

  // One business can hold several concurrent active subscriptions now — one per
  // product family — instead of a single overall "the" subscription.
  const activeSubscriptions = subscriptions.filter(isCurrentlyActive);
  const activeFamilies = new Set(activeSubscriptions.map((s) => s.planFamily));
  const hasUsedTrialForFamily = (family: string) =>
    subscriptions.some((s) => s.planFamily === family && s.isTrial);

  const handleStartPlan = async (planId: string) => {
    if (!selectedBusinessId) return;
    setStartingPlanId(planId);
    setActionError(null);
    try {
      const response = await fetch(`/api/businesses/${selectedBusinessId}/subscriptions`, {
        method: 'POST',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ subscriptionTypeId: planId }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to start subscription');
      }
      await loadData();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to start subscription');
    } finally {
      setStartingPlanId(null);
    }
  };

  const handleCancel = async (subscription: BusinessSubscription) => {
    if (!selectedBusinessId) return;
    if (!window.confirm(`Cancel your ${familyLabel(subscription.planFamily)} subscription? Access continues until the end of the current period.`)) {
      return;
    }
    setCancellingId(subscription.id);
    setActionError(null);
    try {
      const response = await fetch(
        `/api/businesses/${selectedBusinessId}/subscriptions/${subscription.id}/cancel`,
        { method: 'POST', headers: authHeader }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to cancel subscription');
      }
      await loadData();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to cancel subscription');
    } finally {
      setCancellingId(null);
    }
  };

  const handleCheckout = async (planId: string) => {
    if (!selectedBusinessId) return;
    setCheckingOutPlanId(planId);
    setActionError(null);
    try {
      const response = await fetch(`/api/businesses/${selectedBusinessId}/subscriptions/checkout`, {
        method: 'POST',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({ subscriptionTypeId: planId }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to start checkout');
      }
      sessionStorage.setItem('stripeCheckoutPending', '1');
      window.location.href = data.checkoutUrl;
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to start checkout');
      setCheckingOutPlanId(null);
    }
  };

  const handleManageSubscription = async () => {
    if (!selectedBusinessId) return;
    setPortalLoading(true);
    setActionError(null);
    try {
      const response = await fetch(`/api/businesses/${selectedBusinessId}/subscriptions/portal`, {
        method: 'POST',
        headers: authHeader,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to open the billing portal');
      }
      window.location.href = data.portalUrl;
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to open the billing portal');
      setPortalLoading(false);
    }
  };

  const openRequestForm = (planId: string) => {
    setRequestFormPlanId(planId);
    setRequestStart('');
    setRequestEnd('');
    setRequestError(null);
  };

  const handleSubmitTrapRequest = async (plan: SubscriptionType) => {
    if (!selectedBusinessId) return;
    setRequestSubmitting(true);
    setRequestError(null);
    try {
      const response = await fetch(`/api/businesses/${selectedBusinessId}/trap-rental-requests`, {
        method: 'POST',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subscriptionTypeId: plan.id,
          startDate: requestStart,
          endDate: requestEnd,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit rental request');
      }
      setRequestFormPlanId(null);
      await reloadTrapRequests();
    } catch (err) {
      setRequestError(err instanceof Error ? err.message : 'Failed to submit rental request');
    } finally {
      setRequestSubmitting(false);
    }
  };

  if (!selectedBusinessId) {
    return (
      <Alert severity="info">
        You don't have a business selected yet. Add or join a business first from the
        Businesses page — you'll be able to subscribe once you have one.
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  // Only offer plans for families the business doesn't already have an active
  // subscription in — each family is independent, so e.g. an active Inference +
  // Notification plan doesn't hide the API Access or trap-rental plans.
  const availablePlanTypes = planTypes.filter((plan) => !activeFamilies.has(plan.planFamily));

  const availableCategories = (Object.keys(CATEGORY_META) as CategoryKey[]).filter((key) =>
    CATEGORY_META[key].families.some((f) => availablePlanTypes.some((p) => p.planFamily === f))
  );

  const visiblePlans = !category
    ? []
    : availablePlanTypes.filter((p) => {
        if (!CATEGORY_META[category].families.includes(p.planFamily)) return false;
        if (category === 'TRAP_RENTAL') return p.planFamily === trapFamily;
        return true;
      });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {actionError && <Alert severity="error">{actionError}</Alert>}

      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Current Plans — {selectedBusiness?.name}
          </Typography>
          {activeSubscriptions.length > 0 && (
            <Button
              variant="contained"
              size="small"
              startIcon={<DashboardIcon />}
              onClick={() => {
                closeSettings();
                // Full navigation, not client-side routing — forces
                // RequireActiveSubscription to re-fetch fresh instead of
                // trusting its cached pre-subscription "inactive" status.
                window.location.href = '/dashboard';
              }}
            >
              Go to Dashboard
            </Button>
          )}
        </Box>
        {activeSubscriptions.length > 0 ? (
          <Grid container spacing={2}>
            {activeSubscriptions.map((sub) => (
              <Grid item xs={12} sm={6} key={sub.id}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="overline" color="text.secondary">
                      {familyLabel(sub.planFamily)}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="h6">{sub.subscriptionTypeName}</Typography>
                      <Chip label={sub.status} color="success" size="small" />
                      {sub.autoRenew && <Chip label="Auto-renew" size="small" variant="outlined" />}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Started {new Date(sub.dateStart).toLocaleDateString()}
                      {sub.dateEnd &&
                        ` · ${sub.autoRenew ? 'Renews' : 'Ends'} ${new Date(sub.dateEnd).toLocaleDateString()}`}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    {sub.paymentProvider === 'Stripe' ? (
                      <Button size="small" onClick={handleManageSubscription} disabled={portalLoading}>
                        {portalLoading ? 'Opening…' : 'Manage Subscription'}
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleCancel(sub)}
                        disabled={cancellingId === sub.id}
                      >
                        {cancellingId === sub.id ? 'Cancelling…' : 'Cancel Subscription'}
                      </Button>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Alert severity="warning">No active subscription — choose a plan below to continue using the site.</Alert>
        )}
      </Box>

      {availableCategories.length > 0 && (
        <Box>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Available Plans
          </Typography>
          {!isVerified && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Starting a free trial requires your business's primary user to have a confirmed
              email and phone number — verify both in the Profile tab first.
            </Alert>
          )}

          {!category ? (
            <Grid container spacing={2}>
              {availableCategories.map((key) => {
                const meta = CATEGORY_META[key];
                return (
                  <Grid item xs={12} sm={4} key={key}>
                    <OptionCard
                      icon={meta.icon}
                      title={meta.label}
                      caption={meta.caption}
                      selected={false}
                      size="large"
                      onClick={() => setCategory(key)}
                    />
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Box>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => {
                  setCategory(null);
                  setTrapFamily(null);
                }}
                sx={{ mb: 2 }}
              >
                Back to plans
              </Button>

              {category === 'CAMERA_DETECTION' && (
                <Accordion sx={{ mb: 3 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">How to connect your camera</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                      Supported cameras: <strong>Reolink</strong> (steps below) and TP-Link, which
                      uses a similar FTP settings screen.
                    </Typography>
                    <Typography variant="body2" component="ol" sx={{ pl: 2.5, mb: 0 }}>
                      <li>
                        Add your camera as a device (Devices → Add Device), then open its{' '}
                        <strong>FTP Credentials</strong> tab and create a credential — this gives
                        you a username and password unique to that camera.
                      </li>
                      <li>
                        On the camera: <strong>Settings → Network → Advanced → FTP</strong>. Enter
                        the FTP server address and port your DMOTrak administrator has given you,
                        plus the username and password from step 1.
                      </li>
                      <li>
                        Enable <strong>SSL/TLS (Explicit TLS)</strong> and accept the certificate
                        when the camera prompts you — this keeps uploads encrypted in transit.
                      </li>
                      <li>Save, then take a test photo to confirm it uploads.</li>
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              )}

              {category === 'TRAP_RENTAL' && !trapFamily && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {CATEGORY_META.TRAP_RENTAL.families
                    .filter((f) => availablePlanTypes.some((p) => p.planFamily === f))
                    .map((f) => {
                      const meta = TRAP_SUB_META[f];
                      return (
                        <Grid item xs={6} sm={4} key={f}>
                          <OptionCard
                            icon={meta.icon}
                            title={meta.label}
                            selected={false}
                            onClick={() => setTrapFamily(f)}
                          />
                        </Grid>
                      );
                    })}
                </Grid>
              )}

              {(category !== 'TRAP_RENTAL' || trapFamily) && (
                <Grid container spacing={2}>
                  {visiblePlans.map((plan) => {
                    const isFree = plan.price === 0;
                    const isTrapFamily = TRAP_FAMILIES.has(plan.planFamily);
                    const trialBlocked = plan.isTrial && (hasUsedTrialForFamily(plan.planFamily) || !isVerified);
                    const payable = !isFree && !!plan.stripePriceId;
                    const disabled = (!isFree && !payable) || trialBlocked || startingPlanId !== null;
                    const reason = !isFree && !payable
                      ? 'Card payments are coming soon.'
                      : plan.isTrial && hasUsedTrialForFamily(plan.planFamily)
                      ? `This business has already used its free trial for ${familyLabel(plan.planFamily)}.`
                      : plan.isTrial && !isVerified
                      ? 'Verify your email and phone in the Profile tab first.'
                      : '';

                    const pendingRequest = myTrapRequests.find(
                      (r) => r.planFamily === plan.planFamily && r.status === 'Pending'
                    );
                    const estimatedCost =
                      requestStart && requestEnd ? estimateRentalCost(plan.price, requestStart, requestEnd) : null;

                    return (
                      <Grid item xs={12} sm={6} key={plan.id}>
                        <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                          <CardContent sx={{ flexGrow: 1 }}>
                            <Typography variant="overline" color="text.secondary">
                              {familyLabel(plan.planFamily)}
                            </Typography>
                            <Typography variant="h6">{plan.name}</Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              {plan.price === 0 ? 'Free' : `$${plan.price.toFixed(2)}`}
                              {plan.durationInDays ? ` · ${plan.durationInDays} days` : plan.price > 0 ? ' / month' : ''}
                            </Typography>
                            {plan.description && (
                              <Typography variant="body2" color="text.secondary">
                                {plan.description}
                              </Typography>
                            )}
                          </CardContent>
                          <CardActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
                            {isTrapFamily && !isFree ? (
                              pendingRequest ? (
                                <Alert severity="info" sx={{ width: '100%' }}>
                                  Request submitted — {new Date(pendingRequest.startDate).toLocaleDateString()} to{' '}
                                  {new Date(pendingRequest.endDate).toLocaleDateString()}, est. $
                                  {pendingRequest.estimatedCost.toFixed(2)}. Status: {pendingRequest.status}.
                                </Alert>
                              ) : requestFormPlanId === plan.id ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%' }}>
                                  {requestError && <Alert severity="error">{requestError}</Alert>}
                                  <TextField
                                    label="Start date"
                                    type="date"
                                    size="small"
                                    value={requestStart}
                                    onChange={(e) => setRequestStart(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                  />
                                  <TextField
                                    label="End date"
                                    type="date"
                                    size="small"
                                    value={requestEnd}
                                    onChange={(e) => setRequestEnd(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                  />
                                  {estimatedCost !== null && (
                                    <Typography variant="body2" color="text.secondary">
                                      Estimated cost: ${estimatedCost.toFixed(2)} (estimate only — we'll confirm the
                                      final quote when we're in touch)
                                    </Typography>
                                  )}
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button size="small" onClick={() => setRequestFormPlanId(null)} disabled={requestSubmitting}>
                                      Cancel
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      fullWidth
                                      disabled={!requestStart || !requestEnd || requestEnd <= requestStart || requestSubmitting}
                                      onClick={() => handleSubmitTrapRequest(plan)}
                                    >
                                      {requestSubmitting ? 'Submitting…' : 'Submit Request'}
                                    </Button>
                                  </Box>
                                </Box>
                              ) : (
                                <Button fullWidth variant="contained" onClick={() => openRequestForm(plan.id)}>
                                  Request This Rental
                                </Button>
                              )
                            ) : payable ? (
                              <Button
                                fullWidth
                                variant="contained"
                                disabled={checkingOutPlanId !== null}
                                onClick={() => handleCheckout(plan.id)}
                              >
                                {checkingOutPlanId === plan.id ? 'Redirecting…' : `Subscribe — $${plan.price.toFixed(2)}/mo`}
                              </Button>
                            ) : (
                              <Tooltip title={reason} disableHoverListener={!reason}>
                                <span style={{ width: '100%' }}>
                                  <Button
                                    fullWidth
                                    variant="contained"
                                    disabled={disabled}
                                    onClick={() => handleStartPlan(plan.id)}
                                  >
                                    {startingPlanId === plan.id ? 'Starting…' : `Start ${plan.name}`}
                                  </Button>
                                </span>
                              </Tooltip>
                            )}
                          </CardActions>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default BillingSubscriptionTab;
